import { formatDistanceToNow } from "date-fns";
import { Download, Heart, FileText, File, Sheet, Calendar, Loader2, CheckCircle, AlertCircle, User, ExternalLink, Image, Video, Music, Archive, BookOpen, FileSpreadsheet, Presentation, ShieldAlert } from "lucide-react";
import React from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/app/_module/components/ui/card";
import { Progress } from "@/app/_module/components/ui/progress";
import { useToast } from "@/app/_module/hooks/use-toast";
import { cn } from "@/app/_module/lib/utils";

import { useLikeResource, useUnlikeResource, useTrackDownload } from "../hooks/use-hook";
import { useDownload } from "../hooks/useDownload";
import type { ResourceType, ResourceCategory } from "../types/types";

interface DocumentGridProps {
  documents: ResourceType[];
  carriers: { id: string; name: string }[];
  categories: ResourceCategory[];
}

const fileTypeIcons: Record<string, typeof FileText> = {
  // Documents
  pdf: FileText,
  doc: File,
  docx: File,
  txt: FileText,
  rtf: FileText,
  // Spreadsheets
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  csv: FileSpreadsheet,
  ods: FileSpreadsheet,
  // Presentations
  ppt: Presentation,
  pptx: Presentation,
  odp: Presentation,
  // OpenDocument text
  odt: File,
  // Images
  jpg: Image,
  jpeg: Image,
  png: Image,
  gif: Image,
  webp: Image,
  svg: Image,
  // Video
  mp4: Video,
  webm: Video,
  mov: Video,
  // Audio
  mp3: Music,
  wav: Music,
  m4a: Music,
  // Archives
  zip: Archive,
  // eBooks
  epub: BookOpen,
  // Legacy
  form: FileText,
};

// File type badge colors for visual distinction
const fileTypeBadgeColors: Record<string, string> = {
  // Documents - Red
  pdf: "bg-red-100 text-red-800",
  // Word docs - Blue
  doc: "bg-blue-100 text-blue-800",
  docx: "bg-blue-100 text-blue-800",
  odt: "bg-blue-100 text-blue-800",
  // Text - Gray
  txt: "bg-gray-100 text-gray-800",
  rtf: "bg-gray-100 text-gray-800",
  // Spreadsheets - Green
  xls: "bg-green-100 text-green-800",
  xlsx: "bg-green-100 text-green-800",
  csv: "bg-green-100 text-green-800",
  ods: "bg-green-100 text-green-800",
  // Presentations - Orange
  ppt: "bg-orange-100 text-orange-800",
  pptx: "bg-orange-100 text-orange-800",
  odp: "bg-orange-100 text-orange-800",
  // Images - Purple
  jpg: "bg-purple-100 text-purple-800",
  jpeg: "bg-purple-100 text-purple-800",
  png: "bg-purple-100 text-purple-800",
  gif: "bg-purple-100 text-purple-800",
  webp: "bg-purple-100 text-purple-800",
  svg: "bg-purple-100 text-purple-800",
  // Video - Pink
  mp4: "bg-pink-100 text-pink-800",
  webm: "bg-pink-100 text-pink-800",
  mov: "bg-pink-100 text-pink-800",
  // Audio - Indigo
  mp3: "bg-indigo-100 text-indigo-800",
  wav: "bg-indigo-100 text-indigo-800",
  m4a: "bg-indigo-100 text-indigo-800",
  // Archives - Yellow
  zip: "bg-yellow-100 text-yellow-800",
  // eBooks - Teal
  epub: "bg-teal-100 text-teal-800",
};

export const DocumentGrid = function({
  documents,
  carriers,
  categories,
}: DocumentGridProps) {
  const { toast } = useToast();
  const likeMutation = useLikeResource();
  const unlikeMutation = useUnlikeResource();
  const trackDownloadMutation = useTrackDownload();
  const { 
    downloadState, 
    downloadSingle, 
    resetDownloadState, 
    getResourceDownloadState,
    resetResourceDownload 
  } = useDownload();

  const getCarrierName = (carrierId: string) => {
    return carriers.find((c) => c.id === carrierId || c.name === carrierId)?.name || carrierId || "Unknown Carrier";
  };

  const getCategoryName = (categoryId: string) => {
    return (
      categories.find((c) => c.id === categoryId || c.name === categoryId)?.name || categoryId || "Unknown Category"
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      const kBytes = Math.round(bytes / 1024);
      return `${kBytes} KB`;
    }
    const mBytes = Math.round(bytes / (1024 * 1024));
    return `${mBytes} MB`;
  };

  const formatLastUpdated = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Unknown";
      }
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {

      return "Unknown";
    }
  };

  const handleLikeToggle = (document: ResourceType) => {
    // Use .mutate() instead of .mutateAsync() for instant optimistic updates
    // The UI will update immediately via onMutate hook, no need to wait for server
    if (document.isLiked) {
      unlikeMutation.mutate(document.id);
    } else {
      likeMutation.mutate(document.id);
    }
    // Error handling and rollback are done automatically in the mutation hooks
  };

  const handleView = async (document: ResourceType) => {
    if (!document.fileUrl) {
      toast({
        title: "View Error",
        description: "Resource not available for viewing.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Fetch the file and create a blob URL to hide the Supabase URL
      const response = await fetch(document.fileUrl);

      if (!response.ok) {
        throw new Error('Failed to load file');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Open blob URL in new tab - hides the database URL from the user
      window.open(blobUrl, '_blank', 'noopener,noreferrer');

      // Clean up blob URL after a delay to allow the new tab to load
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      toast({
        title: "View Error",
        description: "Failed to load file for viewing.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (document: ResourceType) => {
    try {
      if (!document.id || !document.fileUrl) {
        toast({
          title: "Download Error",
          description: "No download URL available for this document.",
          variant: "destructive",
        });
        return;
      }

      const filename = `${document.title}.${document.fileType.toLowerCase()}`;

      // Track download count first (optimistic update)
      // This will immediately increment the download count in the UI
      trackDownloadMutation.mutate(document.id);

      // Use the Supabase signed URL directly - it already has authentication via token in URL
      // The downloadUtils.ts now correctly infers MIME type from filename extension
      // This fixes corrupted images/videos issue
      await downloadSingle(document.fileUrl, filename, document.id);

      // Show success toast
      toast({
        title: "Download Started",
        description: `Downloading ${document.title}...`,
      });

    } catch (error) {
      toast({
        title: "Download Failed",
        description: error instanceof Error ? error.message : "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const getDownloadButtonContent = (document: ResourceType) => {
    const resourceDownloadState = getResourceDownloadState(document.id);
    
    if (resourceDownloadState?.isDownloading) {
      return (
        <React.Fragment>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Downloading...
        </React.Fragment>
      );
    }

    if (resourceDownloadState?.error) {
      return (
        <React.Fragment>
          <AlertCircle className="h-4 w-4 mr-2" />
          Retry Download
        </React.Fragment>
      );
    }

    if (resourceDownloadState?.isCompleted) {
      return (
        <React.Fragment>
          <CheckCircle className="h-4 w-4 mr-2" />
          Downloaded
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Download className="h-4 w-4 mr-2" />
        Download ({document.downloadCount || 0})
      </React.Fragment>
    );
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">
          No documents found matching your criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Download Progress Bar */}
      {!!downloadState.isDownloading && (
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Downloading...</span>
            <span className="text-sm text-muted-foreground">{downloadState.progress}%</span>
          </div>
          <Progress className="w-full" value={downloadState.progress} />
        </div>
      )}

      {/* Error Message */}
      {!!downloadState.error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-sm text-destructive">{downloadState.error}</span>
            <Button
              className="ml-auto"
              onClick={resetDownloadState}
              size="sm"
              variant="outline"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.map((document) => {
          const fileTypeKey = document.fileType.toLowerCase();
          const IconComponent = fileTypeIcons[fileTypeKey] || FileText;
          const isLiked = Boolean(document.isLiked);
          const likeCount = document.likeCount ?? 0;
          const downloadCount = document.downloadCount ?? 0;
          const isLikeLoading = likeMutation.isPending || unlikeMutation.isPending;
          const resourceDownloadState = getResourceDownloadState(document.id);

          return (
            <Card key={document.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="relative">
                      <IconComponent className="h-5 w-5 text-primary flex-shrink-0" />
                      {document.isSensitive && (
                        <span title={document.autoDetectedSensitive ? "Auto-detected sensitive content" : "Marked as sensitive"}>
                          <ShieldAlert className="h-3 w-3 text-amber-600 absolute -top-1 -right-1" />
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm truncate" title={document.title}>
                        {document.title}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate" title={getCarrierName(document.carrier)}>
                        {getCarrierName(document.carrier)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {likeCount > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {likeCount}
                      </span>
                    )}
                    <Button 
                      className="h-8 w-8" 
                      disabled={isLikeLoading} 
                      onClick={() => handleLikeToggle(document)}
                      size="icon"
                      variant="ghost"
                    >
                      <Heart 
                        className={cn(
                          "h-4 w-4 transition-colors",
                          isLiked 
                            ? "fill-red-500 text-red-500" 
                            : "text-muted-foreground hover:text-red-500"
                        )} 
                      />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="py-2">
                {!!document.description && (
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2" title={document.description}>
                    {document.description}
                  </p>
                )}

                <div className="flex flex-wrap gap-1 mb-2">
                  <Badge className="text-xs" variant="outline">
                    {getCategoryName(document.categoryId)}
                  </Badge>
                  <Badge
                    className={cn(
                      "text-xs",
                      fileTypeBadgeColors[document.fileType.toLowerCase()] || ""
                    )}
                    variant="secondary"
                  >
                    {document.fileType.toUpperCase()}
                  </Badge>
                  {!!document.featured && (
                    <Badge className="text-xs" variant="secondary">
                      Popular
                    </Badge>
                  )}
                  {/* Scope visibility badge */}
                  {document.scope === 'personal' && (
                    <Badge className="text-xs bg-gray-100 text-gray-700" variant="secondary">
                      Personal
                    </Badge>
                  )}
                  {document.scope === 'agency-only' && (
                    <Badge className="text-xs bg-primary/10 text-primary" variant="secondary">
                      My Agency
                    </Badge>
                  )}
                  {document.scope === 'agency-and-below' && (
                    <Badge className="text-xs bg-secondary/10 text-secondary" variant="secondary">
                      Agency + Subs
                    </Badge>
                  )}
                  {document.scope === 'agency-wide' && (
                    <Badge className="text-xs bg-primary/10 text-primary" variant="secondary">
                      Agency-Wide
                    </Badge>
                  )}
                  {document.isSensitive && (
                    <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-300" variant="secondary">
                      <ShieldAlert className="h-3 w-3 mr-1" />
                      Sensitive
                    </Badge>
                  )}
                  {!!isLiked && (
                    <Badge className="text-xs bg-red-100 text-red-800" variant="secondary">
                      Liked
                    </Badge>
                  )}
                  {downloadCount > 5 && (
                    <Badge className="text-xs bg-green-100 text-green-800" variant="secondary">
                      Popular Download
                    </Badge>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatLastUpdated(document.updatedAt)}
                  </span>
                  {!!document.size && <span>{formatFileSize(document.size)}</span>}
                  {downloadCount > 0 && (
                    <span className="flex items-center gap-1">
                      <Download className="h-3 w-3" />
                      {downloadCount} downloads
                    </span>
                  )}
                  {!!document.uploadedBy && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {document.uploadedBy}
                    </span>
                  )}
                </div>

                {/* Per-resource progress bar */}
                {!!resourceDownloadState?.isDownloading && (
                  <div className="mt-2">
                    <Progress className="h-2" value={resourceDownloadState.progress} />
                  </div>
                )}

                {/* Per-resource error display */}
                {!!resourceDownloadState?.error && (
                  <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-destructive">{resourceDownloadState.error}</span>
                      <Button
                        className="h-6 w-6 p-0"
                        onClick={() => resetResourceDownload(document.id)}
                        size="sm"
                        variant="ghost"
                      >
                        ×
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>

              <CardFooter className="pt-2 flex gap-2">
                {/* Show View button for PDFs, images, and videos since they can be viewed directly in browser */}
                {['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'mp4', 'webm', 'mov'].includes(document.fileType.toLowerCase()) && (
                  <Button
                    className="flex-1"
                    disabled={!document.fileUrl}
                    onClick={() => handleView(document)}
                    size="sm"
                    variant="outline"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View
                  </Button>
                )}
                <Button
                  className="flex-1"
                  disabled={resourceDownloadState?.isDownloading}
                  onClick={() => handleDownload(document)}
                  size="sm"
                  variant={resourceDownloadState?.error ? "destructive" : "default"}
                >
                  {getDownloadButtonContent(document)}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
};